import * as R from 'ramda';
import React from 'react';
import classnames from 'classnames';
import { string, func, node, bool, symbol } from 'prop-types';

// eslint-disable-next-line import/no-extraneous-dependencies
import {
  MDCTextFieldFoundation,
  MDCTextFieldIconFoundation,
} from '@material/textfield/dist/mdc.textfield';

import LineRipple from '../lineRipple';
import { Label } from '../form';
import Icon from '../icon';
import Outline from './outline';

// TODO: helper text
class Textfield extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      classes: [],
    };

    this.onAutoFillStart = this.onAutoFillStart.bind(this);
    this.getLabelFoundation = this.getLabelFoundation.bind(this);
    this.getLineRippleFoundation = this.getLineRippleFoundation.bind(this);
    this.getOutlineFoundation = this.getOutlineFoundation.bind(this);

    if (props.icon) {
      this.iconFoundation = new MDCTextFieldIconFoundation({
        setAttr: (attr, value) => this.icon && this.icon.setAttribute(attr, value),
        registerEventHandler: (evtType, handler) =>
          this.icon.addEventListener(evtType, handler),
        deregisterEventHandler: (evtType, handler) =>
          this.icon.removeEventListener(evtType, handler),
        // notifyIconAction: () => null,
      });
    }

    this.foundation = new MDCTextFieldFoundation({
      addClass: (className) => {
        this.setState(({ classes }) => ({
          classes: R.append(className, classes),
        }));
      },
      removeClass: (className) => {
        this.setState(({ classes }) => ({
          classes: R.reject(R.equals(className), classes),
        }));
      },
      hasClass: className => this.textfield.classList.contains(className),
      registerTextFieldInteractionHandler: (evtType, handler) => {
        this.textfield.addEventListener(evtType, handler);
      },
      deregisterTextFieldInteractionHandler: (evtType, handler) => {
        this.textfield.removeEventListener(evtType, handler);
      },
      registerInputInteractionHandler: (evtType, handler) => {
        this.input.addEventListener(evtType, handler);
      },
      deregisterInputInteractionHandler: (evtType, handler) => {
        this.input.removeEventListener(evtType, handler);
      },
      getNativeInput: () => this.input,
      isFocused: () => document.activeElement === this.input,
      isRtl: () => window.getComputedStyle(this.textfield).getPropertyValue('direction') === 'rtl',
      ...this.getLabelFoundation(),
      ...this.getLineRippleFoundation(),
      ...this.getOutlineFoundation(),
    },

    // external foundations
    {
      icon: this.iconFoundation,
    });
  }


  componentDidMount() {
    if (this.props.autoFocus && this.input) {
      this.input.focus();
    }

    this.foundation.init();

    if (this.iconFoundation) {
      this.iconFoundation.init();
    }

    if (this.input && this.input.addEventListener) {
      this.input.addEventListener('animationstart', (e) => {
        if (e.animationName === 'onAutoFillStart') {
          this.onAutoFillStart();
        }
      });
    }
  }

  componentWillUnmount() {
    this.foundation.destroy();

    if (this.iconFoundation) {
      this.iconFoundation.destroy();
    }
  }

  onAutoFillStart() {
    if (this.inputLabel) {
      this.inputLabel.foundation.float(true);
    }
  }

  getLabelFoundation() {
    return {
      shakeLabel: shouldShake => this.inputLabel && this.inputLabel.foundation.shake(shouldShake),
      floatLabel: shouldFloat => this.inputLabel && this.inputLabel.foundation.float(shouldFloat),
      hasLabel: () => Boolean(this.props.label),
      getLabelWidth: () => this.inputLabel && this.inputLabel.foundation.getWidth(),
    };
  }

  getLineRippleFoundation() {
    return {
      activateLineRipple: () => {
        if (this.lineRipple) {
          this.lineRipple.foundation.activate();
        }
      },
      deactivateLineRipple: () => {
        if (this.lineRipple) {
          this.lineRipple.foundation.deactivate();
        }
      },
      setLineRippleTransformOrigin: (normalizedX) => {
        if (this.lineRipple) {
          this.lineRipple.foundation.setRippleCenter(normalizedX);
        }
      },
    };
  }

  getOutlineFoundation() {
    return {
      notchOutline: (labelWidth, isRtl) => this.outline.foundation.notch(labelWidth, isRtl),
      closeOutline: () => this.outline.foundation.closeNotch(),
      hasOutline: () => Boolean(this.props.outline),
    };
  }

  render() {
    const {
      className,
      inputRef,
      labelAlign,
      label,
      inline,
      autoFocus,
      fullWidth,
      disabled,
      dense,
      icon,
      outline,
      invalid,
      ...props
    } = this.props;

    return (
      <div
        ref={(textfield) => {
          this.textfield = textfield;
        }}
        className={classnames(
          'mdc-text-field',
          'mdc-text-field--upgraded',
          {
            'mdc-text-field__input--row-reverse': labelAlign === Label.ALIGN_RIGHT,
            'mdc-text-field--inline': inline,
            'mdc-text-field--no-label': !label,
            'mdc-text-field--fullwidth': fullWidth,
            'mdc-text-field--disabled': disabled,
            'mdc-text-field--dense': dense,
            'mdc-text-field--with-leading-icon': Boolean(icon),
            'mdc-text-field--outlined': outline,
            'mdc-text-field--invalid': invalid,
          },
          ...this.state.classes,
          className,
        )}
      >
        {label && (
          <Label
            htmlFor={props.id}
            ref={(inputLabel) => {
              this.inputLabel = inputLabel;
            }}
          >{label}
          </Label>
        )}
        {icon && (
          <Icon className="mdc-text-field__icon" tabindex="-1">{icon}</Icon>
        )}
        <input
          ref={(input) => {
            this.input = input;
            inputRef(input);
          }}
          className="mdc-text-field__input"
          disabled={disabled}
          {...props}
        />
        <LineRipple ref={(lineRipple) => { this.lineRipple = lineRipple; }} />
        {outline && (
          <Outline ref={(element) => { this.outline = element; }} />
        )}
      </div>
    );
  }
}

Textfield.propTypes = {
  className: string,
  inputRef: func,
  labelAlign: symbol,
  label: node,
  inline: bool,
  autoFocus: bool,
  fullWidth: bool,
};

Textfield.defaultProps = {
  className: undefined,
  inputRef: () => {},
  labelAlign: Label.ALIGN_LEFT,
  label: null,
  inline: false,
  autoFocus: false,
  fullWidth: false,
};

export default Textfield;
